import React, { useContext, ReactNode, ComponentType } from 'react';

interface FocusSettings {
  ttsOnFocusEnabled: boolean;
}

const FocusSettingsContext = React.createContext<FocusSettings>({ ttsOnFocusEnabled: true });

interface FocusSettingsProviderProps {
  children?: ReactNode;
  enableTTSOnFocus?: boolean;
}

export const FocusSettingsProvider = ({
  children = null,
  enableTTSOnFocus = true,
}: FocusSettingsProviderProps) => {
  return (
    <FocusSettingsContext.Provider
      value={{ ttsOnFocusEnabled: enableTTSOnFocus }}
    >
      {children}
    </FocusSettingsContext.Provider>
  );
};

interface FocusSettingsConsumerProps {
  children: (value: FocusSettings) => ReactNode;
}

export const FocusSettingsConsumer = ({
  children = () => null,
}: FocusSettingsConsumerProps) => {
  return (
    <FocusSettingsContext.Consumer>
      {value => children(value)}
    </FocusSettingsContext.Consumer>
  );
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const withFocusSettings = <P extends Record<string, any>>(
  Component: ComponentType<P & FocusSettings>,
): ComponentType<Omit<P, keyof FocusSettings>> => {
  const WithFocusSettings = (props: Omit<P, keyof FocusSettings>) => {
    return (
      <FocusSettingsConsumer>
        {value => <Component {...(props as P)} {...value} />}
      </FocusSettingsConsumer>
    );
  };

  WithFocusSettings.displayName = `withFocusSettings(${Component.displayName || Component.name || 'Component'})`;

  return WithFocusSettings;
};

export const useFocusSettings = (): FocusSettings => {
  return useContext(FocusSettingsContext);
};
